<script setup lang="ts">
import TripPricing from './TripPricing.vue';
import CapacityText from './CapacityText.vue';
import PackageDetails from './package-details/PackageDetails.vue';
import TrackingButton from './TrackingButton.vue';
import LabelView from '@components/util/LabelView.vue';
import SegmentView from '@components/util/trip/SegmentView.vue';
import type TripPackage from '@itinerary/trip/TripPackage';

const {
    tripPackage,
    hideCapacity,
    hideInvoice,
    customLabel,
    isSelected,
    showPriceOnButton,
    isOpenDetails,
} = defineProps<IProps>();
const emit = defineEmits<{
    'show-details': [];
    'hide-details': [];
    'button-clicked': [pkg: TripPackage];
    mouseenter: [event: MouseEvent];
    mouseleave: [event: MouseEvent];
}>();
const searchFormStore = useSearchFormStore();
const authStore = useAuthStore();
const analyticsStore = useAnalyticsStore();
interface IProps {
    tripPackage: TripPackage;
    isOpenDetails?: boolean;
    isSelected?: boolean;
    customLabel?: string;
    showPriceOnButton?: boolean;
    hideInvoice?: boolean;
    hideCapacity?: boolean;
}
const gtm = useGtm();
const i18n = useI18n();
const { isMobile } = useSize();
const isHovered = ref(false);
const showingDetails = computed(() => isOpenDetails);
const cardLabel = computed(() => customLabel || tripPackage.label);
const capacityText = computed(() =>
    tripPackage.capacity === 0
        ? ''
        : tripPackage.capacityTextReplacement ||
          i18n.t('Card.seatCapacity', {
              capacityText: tripPackage.capacityText,
          })
);
const showTrackingButton = computed(
    () => !tripPackage.isForBundlePackage && !isSelected
);
const maxHeight = computed(
    () => `${tripPackage.allGroupedTrips.length * 115 + 200}px`
);
const toggleDetails = () => {
    if (isSelected) {
        return;
    }
    if (!showingDetails.value) {
        showDetails();
    } else {
        hideDetails();
    }
};
const showDetails = () => {
    if (isSelected) {
        return;
    }
    gtm?.trackEvent({ event: 'view_item' });
    analyticsStore.trackAll('details', {
        isDiscounted: `${!!tripPackage.selectedPrice?.passengerFares[0]
            ?.discount}`,
        isFeatured: 'false',
        isSameService: `${
            tripPackage.reserveType === searchFormStore.firstReserveType
        }`,
        isMasir: 'false',
        creationSource: authStore.siteSourceId,
        whiteLabel: useSiteConfigs().title,
    });
    if (isMobile.value) {
        open();
    }
    emit('show-details');
};
const hideDetails = () => {
    if (isMobile.value) {
        close();
    }
    emit('hide-details');
};
const reserve = () => {
    if (!tripPackage.reserveButtonEnabled && !tripPackage.autoReserveEnabled) {
        swal({
            icon: 'warning',
            title: tripPackage?.alertText,
            confirmButtonText: i18n.t('CardTermsAndConditions.ok'),
            text: tripPackage.reserveError,
        });
        return;
    }
    emitReserve();
};

const { open, close } = useModal({
    component: PackageDetails,
    modalAttrs: {
        zIndexFn: () => 100000000,
    },
    attrs: {
        hideInvoice,
        tripPackage,
        onReserve: reserve,
        onHideDetails: hideDetails,
        style: { '--max-height': maxHeight },
    },
});

const emitReserve = () => emit('button-clicked', tripPackage);
const onHoverIn = (event: MouseEvent) => {
    isHovered.value = true;
    emit('mouseenter', event);
};
const onHoverOut = (event: MouseEvent) => {
    isHovered.value = false;
    emit('mouseleave', event);
};
</script>

<template>
    <div
        class="trip-card-wrapper"
        :class="{ 'non-reservable': !tripPackage.reservable }"
    >
        <div
            class="trip-card-container"
            :class="{ labeled: cardLabel }"
            @click="toggleDetails"
            @mouseenter="onHoverIn"
            @mouseleave="onHoverOut"
        >
            <div v-if="cardLabel" class="card-label">{{ cardLabel }}</div>
            <section class="info-section">
                <SegmentView
                    v-for="(segment, key) in tripPackage.segments"
                    :key
                    :segment
                    :vehicle-type="tripPackage.vehicleType"
                    :international="tripPackage.international"
                />
                <div class="details-container">
                    <mr-dropdown
                        v-if="!isSelected"
                        class="details-button"
                        :model-value="showingDetails"
                        variant="transparent"
                        :title="showingDetails ? 'بستن' : 'جزئیات'"
                        only-button
                        @update:model-value="toggleDetails"
                    />
                    <template v-if="!isSelected">
                        <LabelView
                            v-for="label in tripPackage.labels"
                            :key="label.title"
                            :label
                        />
                    </template>
                    <CapacityText
                        v-if="
                            tripPackage.reservable &&
                            !showPriceOnButton &&
                            !hideCapacity
                        "
                        :capacity-text
                        :capacity-status="tripPackage.capacityStatus"
                        :desktop-only="!showPriceOnButton"
                    />
                </div>
            </section>
            <transition name="details">
                <mr-divider v-if="showingDetails" vertical :thickness="1" />
            </transition>
            <section v-if="!isSelected" class="reserve-section">
                <div
                    class="info-container"
                    :class="{ 'price-top-button': !showPriceOnButton }"
                >
                    <CapacityText
                        v-if="tripPackage.reservable && !hideCapacity"
                        :capacity-text
                        :capacity-status="tripPackage.capacityStatus"
                        :mobile-only="!showPriceOnButton"
                    />
                    <TrackingButton
                        v-if="showTrackingButton"
                        :trip-package
                        :show-tooltip="!tripPackage.reservable && isHovered"
                    />
                    <TrackingButton
                        v-if="tripPackage.canPriceAutoReserve && !isSelected"
                        :trip-package
                        type="reserve"
                        mobile-hide-tooltip
                    />
                </div>
                <slot name="pricing">
                    <TripPricing
                        :trip-package
                        :is-selected
                        :button-price="showPriceOnButton"
                        @reserve="reserve"
                    />
                </slot>
            </section>
        </div>
        <transition name="details">
            <PackageDetails
                v-if="showingDetails && !isMobile"
                :trip-package
                :style="{ '--max-height': maxHeight }"
                :hide-invoice
                @reserve="reserve"
            />
        </transition>
    </div>
</template>

<style scoped lang="scss">
.details-leave-active {
    animation: 500ms change-animation reverse;
    @include mobile-only {
        animation: 200ms m-change-animation reverse;
    }
}

.details-enter-active {
    animation: 500ms change-animation;
    @include mobile-only {
        animation: 200ms m-change-animation;
    }
}

@keyframes m-change-animation {
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0%);
    }
}
@keyframes change-animation {
    from {
        max-height: 0;
    }
    to {
        max-height: var(--max-height);
    }
}

.trip-card-wrapper {
    @include shadow();
    display: flex;
    flex-direction: column;
    background-color: white;
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    transition: all 200ms;
    &.non-reservable {
        background-color: usePalette($p-gray, 100);
    }
}

.trip-card-container {
    display: flex;
    position: relative;
    @include mobile-only() {
        flex-direction: column;
    }
    &.labeled {
        @include desktop-only {
            padding-top: 32px;
        }
    }
    .card-label {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: usePalette($p-yellow, 100);
        padding: 2px;
        // text
        @include tg-s($w7, usePalette($p-yellow, $p-700));
        text-align: center;
        white-space: nowrap;
        @include desktop-only {
            position: absolute;
            top: 0;
        }
        height: 32px;
        width: 100%;
        // text
    }

    .info-section {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        padding: 4px 0;
        overflow: hidden;
        @include mobile-only() {
            padding: 12px;
        }
        .details-container {
            display: flex;
            align-items: center;
            order: 1;
            padding-right: 20px;
            :deep() {
                > * {
                    margin: 0 0 0 4px;
                }
            }
            .details-button {
                width: 100px;
                margin-left: 20px;
            }
            .points {
                padding: 8px;
            }
            @include mobile-only() {
                order: 0;
                .details-button {
                    display: none;
                }
                .points {
                    margin-right: auto;
                    padding: 0;
                }
            }
        }
    }

    .reserve-section {
        position: relative;
        flex: 0 0 230px;
        display: flex;
        flex-direction: column;
        padding: 12px;
        width: 230px;
        @include mobile-only() {
            flex: auto;
            flex-direction: row;
            align-items: center;
            height: 48px;
            width: 100%;
            padding: 8px 8px 8px 12px;
            border-top: 1px solid usePalette($p-blue, 100);
        }

        .info-container {
            align-self: stretch;
            display: flex;
            justify-content: flex-end;
            @include desktop-only() {
                &.price-top-button {
                    position: absolute;
                    left: 16px;
                }
            }
            @include mobile-only() {
                flex-direction: row-reverse;
                align-items: center;
            }
        }
    }
}
</style>
